import React from 'react'
/** @type {import('eplayer-core').Media} */
import { Media } from 'eplayer-core'

import { csvToRows } from 'polyp'
import { csvToEdition } from 'polyp'
import { isParseError } from 'polyp'
import { modernTemplates } from 'polyp'
import { legacyTemplates } from 'polyp'
import { parseFrameWorkRun } from 'polyp'
import { parseChapterRuns } from 'polyp'

import { read as readExcel } from 'xlsx'
import { utils as utilsExcel } from 'xlsx'

import { Captures as TID } from "@common/ui.constants"
import { uploadMediaFiles } from '@common/media'
import { io } from '@common/io'

import { useMultiFileInput } from '../../hooks/file-input.ts'

import { HEADER_GRADIENT } from './manager-header'
import { ManagerHeader } from './manager-header'
import { ManagerBreadcrumb } from './manager-breadcrumb'
import { ManagerAction } from './manager-action'
import { ManagerSelect } from './manager-select'

const BATCH_MEDIA_ACCEPT = Object.freeze([
  '.jpg',
  '.png',
  '.svg',
  '.mp3',
  '.mp4',
  '.js',
  '.xls',
  '.xlsx',
])

/**
 * @type {{
 *   readonly SETUP: 'SETUP',
 *   readonly READY: 'READY',
 *   readonly PROCESSING: 'PROCESSING',
 *   readonly DONE: 'DONE'
 * }}
 */
const PROCESS_STAGE = {
  SETUP: 'SETUP',
  READY: 'READY',
  PROCESSING: 'PROCESSING',
  DONE: 'DONE'
}

/**
 * @type {{
 *   readonly EN: 'en',
 *   readonly ES: 'es'
 * }}
 */
const LANGUAGE = {
  EN: 'en',
  ES: 'es'
}

/**
 * @type {{
 *   readonly CHANGE_NAME: 'CHANGE_NAME',
 *   readonly RESET_NAME: 'RESET_NAME',
 *   readonly CHANGE_LANGUAGE: 'CHANGE_LANGUAGE',
 *   readonly TOGGLE_PROFILE: 'TOGGLE_PROFILE',
 *   readonly ADD_FILES: 'ADD_FILES',
 *   readonly RESET_FILES: 'RESET_FILES',
 *   readonly RAISE_EXCEL_ERR: 'RAISE_EXCEL_ERR',
 *   readonly RAISE_MISSING_FILES: 'RAISE_MISSING_FILES',
 *   readonly VALIDATE_FILES: 'VALIDATE_FILES',
 *   readonly START_PROCESS: 'START_PROCESS',
 *   readonly STOP_PROCESS: 'STOP_PROCESS',
 *   readonly DONE_PROCESS: 'DONE_PROCESS',
 *   readonly STEP_PROCESS: 'STEP_PROCESS',
 *   readonly RAISE_NETWORK_ERR: 'RAISE_NETWORK_ERR',
 *   readonly RAISE_PROCESS_ERR: 'RAISE_PROCESS_ERR',
 * }}
 */
const ACTION = {
  CHANGE_NAME: 'CHANGE_NAME',
  RESET_NAME: 'RESET_NAME',
  CHANGE_LANGUAGE: 'CHANGE_LANGUAGE',
  TOGGLE_PROFILE: 'TOGGLE_PROFILE',
  ADD_FILES: 'ADD_FILES',
  RESET_FILES: 'RESET_FILES',
  RAISE_EXCEL_ERR: 'RAISE_EXCEL_ERR',
  RAISE_MISSING_FILES: 'RAISE_MISSING_FILES',
  VALIDATE_FILES: 'VALIDATE_FILES',
  START_PROCESS: 'START_PROCESS',
  STOP_PROCESS: 'STOP_PROCESS',
  DONE_PROCESS: 'DONE_PROCESS',
  STEP_PROCESS: 'STEP_PROCESS',
  RAISE_NETWORK_ERR: 'RAISE_NETWORK_ERR',
  RAISE_PROCESS_ERR: 'RAISE_PROCESS_ERR',
}

/** @typedef {typeof PROCESS_STAGE[keyof typeof PROCESS_STAGE]} ProcessStage */
/** @typedef {typeof ACTION[keyof typeof ACTION]} ActionType */

/**
 * @template {ActionType} T
 * @typedef {T extends 'CHANGE_NAME' ? { type: T, payload: string } :
 *   T extends 'RESET_NAME' ? { type: T } :
 *   T extends 'CHANGE_LANGUAGE' ? { type: T, payload: string } :
 *   T extends 'TOGGLE_PROFILE' ? { type: T } :
 *   T extends 'ADD_FILES' ? { type: T, payload: File[] } :
 *   T extends 'RESET_FILES' ? { type: T } :
 *   T extends 'RAISE_EXCEL_ERR' ? { type: T, payload: string } :
 *   T extends 'RAISE_MISSING_FILES' ? { type: T, payload: ValidationError[] } :
 *   T extends 'VALIDATE_FILES' ? { type: T } :
 *   T extends 'START_PROCESS' ? { type: T } :
 *   T extends 'STOP_PROCESS' ? { type: T } :
 *   T extends 'DONE_PROCESS' ? { type: T, payload: import('eplayer-core').Edition } :
 *   T extends 'STEP_PROCESS' ? { type: T } :
 *   T extends 'RAISE_NETWORK_ERR' ? { type: T, payload: Error | unknown } :
 *   T extends 'RAISE_PROCESS_ERR' ? { type: T, payload: string } :
 *   never
 * } ReducerAction
 */


/**
 * @typedef {Object} ValidationError
 * @property {boolean} hasExpectedAudio
 * @property {boolean} hasExpectedVideo
 * @property {number} rowNum
 * @property {string} rowTemplate
 * @property {string} rowExpectedAudio
 * @property {string} rowExpectedVideo
 */

/** @typedef {{
 *    stage: ProcessStage
 *    files: File[]
 *    filesValid: boolean
 *    excelErr: string
 *    rowErrs: ValidationError[]
 *    networkErr: string
 *    processErr: string
 *    processStepsTotal: number
 *    processStepsDone: number
 *    edition: import('eplayer-core').Edition | null
 *    editionName: string
 *    language: string
 *    newProfile: boolean
 * }} ConversionState
 */

/**
 * @type {ConversionState}
 */
const initState = {
  stage: PROCESS_STAGE.SETUP,
  files: [],
  filesValid: false,
  excelErr: "",
  rowErrs: [],
  networkErr: "",
  processErr: "",
  processStepsTotal: 0,
  processStepsDone: 0,
  edition: null,
  editionName: "",
  language: LANGUAGE.EN,
  newProfile: true,
}

/**
 *  @param {ConversionState} state
 *  @returns {ConversionState}
 */
function deriveReadyStage(state) {
  const isReady =
    state.stage === PROCESS_STAGE.SETUP &&
    state.filesValid &&
    state.editionName !== "" &&
    state.excelErr === "" &&
    state.rowErrs.length === 0 &&
    state.files.length > 0
  if (isReady) return { ...state, stage: PROCESS_STAGE.READY }
  else return state
}

/**
 * @param {ConversionState} state
 * @param {ReducerAction<ActionType>} action
 * @returns {ConversionState}
 */
function reducer(state, action) {
  const { SETUP } = PROCESS_STAGE
  const { READY } = PROCESS_STAGE
  const { PROCESSING } = PROCESS_STAGE
  const { DONE } = PROCESS_STAGE
  switch (action.type) {
    case 'CHANGE_NAME': {
      const editionName = action.payload.trim()
      const isEmpty = editionName === ""
      return deriveReadyStage(
        state.stage === SETUP || state.stage === READY ?
          {
            ...state,
            editionName,
            stage: isEmpty ? SETUP : state.stage,
          }
          :
          state
      )
    }
    case 'RESET_NAME': {
      return deriveReadyStage(
        state.stage === SETUP || state.stage === READY ?
          {
            ...state,
            editionName: "",
            stage: SETUP,
          } :
          state
      )
    }
    case 'CHANGE_LANGUAGE': {
      return deriveReadyStage(
        state.stage === SETUP || state.stage === READY ?
          {
            ...state,
            language: action.payload,
          } :
          state
      )
    }
    case 'TOGGLE_PROFILE': {
      return {
        ...state,
        newProfile: !state.newProfile,
      }
    }
    case 'ADD_FILES': {
      const files = [ ...state.files, ...action.payload ]
      return deriveReadyStage(
        state.stage === SETUP ?
          { ...state, files } :
          state
      )
    }
    case 'RESET_FILES': {
      return deriveReadyStage(
        state.stage  === SETUP || state.stage === READY ?
          {
            ...state,
            excelErr: "",
            rowErrs: [],
            processErr: "",
            networkErr: "",
            files: [],
            filesValid: false,
            stage: SETUP,
          } :
          state
      )
    }
    case 'RAISE_EXCEL_ERR': {
      const excelIdx = state.files.findIndex(f =>
        f.name.endsWith('.xlsx') || f.name.endsWith('.xls'))
      const files =
        [...state.files.slice(0, excelIdx), ...state.files.slice(excelIdx + 1)]
      return deriveReadyStage(
        state.stage === SETUP ?
          {
            ...state,
            files,
            excelErr: action.payload,
            networkErr: "",
            rowErrs: [],
            stage: SETUP,
          } :
          state
      )
    }
    case 'RAISE_MISSING_FILES': {
      return deriveReadyStage(
        state.stage === SETUP ?
          {
            ...state,
            excelErr: "",
            networkErr: "",
            rowErrs: action.payload,
            filesValid: false,
            stage: SETUP,
          } :
          state
      )
    }
    case 'VALIDATE_FILES': {
      return deriveReadyStage(
        state.stage === SETUP ?
          {
            ...state,
            rowErrs: [],
            excelErr: "",
            filesValid: true,
          } :
          state
      )
    }
    case 'START_PROCESS': {
      return state.stage === READY ?
        {
          ...state,
          stage: PROCESSING,
          processStepsTotal: state.files.length + 1,
        } :
        state
    }
    case 'STOP_PROCESS': {
      return state.stage === PROCESSING ?
        { ...state, stage: READY } :
        state
    }
    case 'DONE_PROCESS': {
      if (state.stage !== PROCESSING) return state
      const edition = {
        ...action.payload,
        name: state.editionName
      }
      return {
          ...state,
          stage: DONE,
          edition,
        }
    }
    case 'STEP_PROCESS': {
      if (state.stage !== PROCESSING) return state
      return {
        ...state,
        processStepsDone: state.processStepsDone + 1,
      }
    }
    case 'RAISE_NETWORK_ERR': {
      const msg = action.payload instanceof Error ?
        action.payload.message :
        String(action.payload)
      return {
        ...state,
        stage: READY,
        excelErr: "",
        rowErrs: [],
        processErr: "",
        networkErr: `Network error during upload: ${msg}`,
      }
    }
    case 'RAISE_PROCESS_ERR': {
      const excelIdx = state.files.findIndex(f =>
        f.name.endsWith('.xlsx') || f.name.endsWith('.xls'))
      const files =
        [...state.files.slice(0, excelIdx), ...state.files.slice(excelIdx + 1)]
      return state.stage === PROCESSING ?
        {
          ...state,
          files,
          excelErr: "",
          rowErrs: [],
          networkErr: "",
          processErr: action.payload,
          stage: SETUP,
        } :
        state
    }
    default:
      return state
  }
}

export function Conversion({ className: visibleState, onClickBreadcrumb, app }) {
  /** @type {React.MutableRefObject<AbortController|null>} */
  const abortRef = React.useRef(null)
  /** @type {[ConversionState, React.Dispatch<ReducerAction<ActionType>>]} */
  const [state, dispatch] = React.useReducer(reducer, initState)
  const { stage } = state
  const { edition } = state
  const { editionName } = state
  const { language } = state
  const { newProfile } = state
  const { files } = state
  const { processStepsTotal } = state
  const { processStepsDone } = state
  const { excelErr } = state
  const { rowErrs } = state
  const { networkErr } = state
  const { processErr } = state

  const isSetup = stage === PROCESS_STAGE.SETUP
  const isReady = stage === PROCESS_STAGE.READY
  const isProcessing = stage === PROCESS_STAGE.PROCESSING
  const isDone = stage === PROCESS_STAGE.DONE
  const nameErr = editionName === ""
  const validErr = rowErrs.length > 0
  const hasError =
    excelErr ||
    networkErr ||
    processErr ||
    validErr ||
    nameErr ?
      true : false

  const shouldDisableBatchAdd = !isSetup || nameErr
  const shouldDisableBatchReset = isProcessing || isDone || files.length === 0
  const shouldDisableSubmit = !isReady

  const changeName = (e) => {
    return dispatch({
      type: 'CHANGE_NAME',
      payload: e.target.value,
    })
  }
  const resetName = () => {
    dispatch({ type: 'RESET_NAME' })
  }
  const changeLanguage = (e) => {
    dispatch({
      type: 'CHANGE_LANGUAGE',
      payload: e.target.value,
    })
  }
  const toggleProfile = () => {
    dispatch({ type: 'TOGGLE_PROFILE' })
  }
  const handleSelectedFiles = async (files) => {
    if (!files || files.length === 0) return
    return dispatch({
      type: 'ADD_FILES',
      payload: Array.from(files),
    })
  }
  const resetSelectedFiles = () => {
    dispatch({ type: 'RESET_FILES' })
  }
  const startProcess = async () => {
    dispatch({ type: 'START_PROCESS' })
  }
  const onProcessCancelled = () => {
    abortRef.current?.abort()
    abortRef.current = null
    dispatch({ type: 'STOP_PROCESS' })
  }
  const onProcessError = (err) => {
    const msg = err instanceof Error ? err.message : String(err)
    abortRef.current?.abort()
    abortRef.current = null
    dispatch({
      type: 'RAISE_PROCESS_ERR',
      payload: `Encountered error while attempting conversion: ${msg}`,
    })
  }
  const onProcessDone = (edition) => {
    abortRef.current?.abort()
    abortRef.current = null
    return dispatch({ type: 'DONE_PROCESS', payload: edition })
  }

  const back = React.useCallback(() => {
    onClickBreadcrumb()
  }, [ onClickBreadcrumb ])

  React.useEffect(() => {
    const handleKeyUp = (e) => {
      if (e.key === 'Escape') {
        back()
      }
    }
    document.addEventListener('keyup', handleKeyUp)
    return () => {
      document.removeEventListener('keyup', handleKeyUp)
    }
  }, [back])

  React.useEffect(() => {
    const abortController = new AbortController()
    const findAndValidateExcel = async () => {
      const excelIdx = files.findIndex(f =>
        f.name.endsWith('.xlsx') || f.name.endsWith('.xls'))

      if (excelIdx === -1) return

      const excelFile = files[excelIdx]
      const buffer = await io.fileToArrayBuffer(excelFile)

      if (abortController.signal.aborted) return

      const excelWorkbook = readExcel(buffer)
      if (abortController.signal.aborted) return
      if (!excelWorkbook.Sheets.script) return dispatch({
        type: 'RAISE_EXCEL_ERR',
        payload: 'Excel file is missing a necessary worksheet called "script"'
      })


      const csv = utilsExcel.sheet_to_csv(excelWorkbook.Sheets.script)

      /** @type {import('polyp').CSV[]} */
      const allScriptRows = csvToRows(csv).filter(isRow)
      // N.B. Mutative
      parseFrameWorkRun(allScriptRows)

      const result = parseChapterRuns(allScriptRows)
      if (abortController.signal.aborted) return
      if (isParseError(result)) return dispatch({
        type: 'RAISE_EXCEL_ERR',
        payload: `Encountered an error parsing the script: ${result.message}`
      })

      if (abortController.signal.aborted) return
      if (result.length === 0) return dispatch({
        type: 'RAISE_EXCEL_ERR',
        payload:   "Encountered an error parsing the script: no valid chapter rows found; verify worksheet formatting and add correct Excel file"
      })

      const validationErrs = []
      for (const row of result) {
        const rowExpectedAudio = row.audioFileName || ""
        let rowExpectedVideo = ""
        if (row.graphicFileInfo) {
          const fileInfoParts = row.graphicFileInfo.split(',')
          if (fileInfoParts.length === 3) {
            rowExpectedVideo = fileInfoParts[0].replace('swf', 'mp4')
          }
        }

        const hasExpectedAudio = !rowExpectedAudio ||
          Boolean(files.find(f => f.name === rowExpectedAudio))
        const hasExpectedVideo = !rowExpectedVideo ||
          Boolean(files.find(f => f.name === rowExpectedVideo))

        if (!hasExpectedAudio || !hasExpectedVideo) {
          validationErrs.push({
            hasExpectedAudio,
            hasExpectedVideo,
            rowNum: row.rowNumber,
            rowTemplate: row.template,
            rowExpectedAudio,
            rowExpectedVideo
          })
        }
      }

      if (abortController.signal.aborted) return
      if (validationErrs.length) return dispatch({
        type: 'RAISE_MISSING_FILES',
        payload: validationErrs,
      })

      if (abortController.signal.aborted) return
      return dispatch({ type: 'VALIDATE_FILES' })
    }

    if (state.stage === PROCESS_STAGE.SETUP) findAndValidateExcel()
    return () => {  abortController.abort() }
  }, [files, state.stage])

  React.useEffect(() => {
    abortRef.current = new AbortController()
    const run = async () => {
      try {
        const audioMap = {}
        const videoMap = {}
        const spriteMap = {}
        const templates = { ...legacyTemplates.templates, ...modernTemplates }

        const xlsIdx = files.findIndex(f => f.name.endsWith('.xlsx') || f.name.endsWith('.xls'))
        const xls = files[xlsIdx]
        const buf = await io.fileToArrayBuffer(xls)
        const wkbk = readExcel(buf)
        if (!wkbk.Sheets.script) throw new Error('Excel file is missing a "script" sheet')
        const csv = utilsExcel.sheet_to_csv(wkbk.Sheets.script)

        const filesToUpload = [...files.slice(0, xlsIdx), ...files.slice(xlsIdx + 1)]
        legacyTemplates.spriteMedia.forEach(spr => { spriteMap[spr.checksum] = spr })
        legacyTemplates.audioMedia.forEach(aud => { audioMap[aud.checksum] = aud })

        for await (const [_, result] of uploadMediaFiles(abortRef.current.signal, ...filesToUpload)) {
          if (result instanceof Error) {
            return dispatch({
              type: 'RAISE_NETWORK_ERR',
              payload: result,

            })
          }
          switch (result.fileExt) {
            case 'wave': case 'mp3':
              audioMap[result.fileName] = Media.RawAudio.withDefaults(result)
              break
            case 'mp4':
              videoMap[result.fileName] = Media.RawVideo.withDefaults(result)
              break
            case 'png': case 'jpg': case 'svg':
              spriteMap[result.fileName] = Media.RawSprite.withDefaults(result)
              break
            default:
              throw new Error(`Unsupported media type: ${result.Type ?? String(result)}`)
          }
          if (abortRef.current.signal.aborted) return
          dispatch({ type: 'STEP_PROCESS' })
        }

        const edition = csvToEdition(audioMap, videoMap, spriteMap, templates, csv, language, newProfile)
        if (abortRef.current.signal.aborted) return
        dispatch({ type: 'STEP_PROCESS' })

        if (Array.isArray(edition)) {
          const err = new Error(edition.map(e => e.message).join('\n'))
          onProcessError(err)
        } else {
          onProcessDone(edition)
        }
      } catch (err) {
        const isAbortError =
          err instanceof DOMException && err.name === 'AbortError'
        if (isAbortError) return
        onProcessError(err instanceof Error ? err : new Error(String(err)))
      }
    }

    if (state.stage === PROCESS_STAGE.PROCESSING) run()
    return () => abortRef.current?.abort()
  }, [state.stage, language, newProfile])

  React.useEffect(() => {
    const proceed =
      stage === PROCESS_STAGE.DONE
    if (proceed) {
      if (!edition) throw new Error(`Expected populated Edition in DONE stage`)
      app.readEdition(state.edition)
    }
  }, [stage, edition])

  let statusVDom
  if (isSetup && !hasError) statusVDom = <MissingScriptError />
  else if (isSetup && nameErr) statusVDom = <MissingNameError />
  else if (isSetup && excelErr) statusVDom = <IOError message={excelErr} />
  else if (isSetup && processErr) statusVDom = <IOError message={processErr} />
  else if (isSetup && validErr) statusVDom = <ValidationErrors errors={rowErrs} />
  else if (isReady && !hasError) statusVDom = <ConversionReady />
  else if (isReady && networkErr) statusVDom = <NetworkError message={networkErr} />
  else statusVDom = <Process
    completed={processStepsDone}
    total={processStepsTotal}
    onCancel={onProcessCancelled}
  />

  const vDom =
    <editiondetails-wrapper class={visibleState}>
      <editiondetails-container>
        <ManagerBreadcrumb onClick={back} testId={TID.BACK}/>
        <ManagerHeader title="Convert a Script to Edition" gradient={HEADER_GRADIENT.LIGHTBLUE_BLUE} style={{ marginBottom: '80px'}}></ManagerHeader>
        <section className="manager-section manager-conversion">
          <div className="manager-search__query">
            <input
              id="manager-search__input"
              style={{background: 'none', width: '350px' }}
              type="text"
              placeholder="Name this edition"
              data-qa-hook="input.searchBar"
              value={editionName}
              onChange={changeName}
            />
            <button className="manager-search__reset" onClick={resetName} />
          </div>
          <div className="manager-search__actions">
            <ToggleProfile
              onClick={toggleProfile}
              disabled={isProcessing}
              boolean={newProfile}
            />
            <ManagerSelect
              autoName="conversion.select.language"
              label="Language:"
              value={language}
              onChange={changeLanguage}
              disabled={isProcessing}
              options={[
                { value: LANGUAGE.EN, label: 'English' },
                { value: LANGUAGE.ES, label: 'Spanish' },
              ]}
            />
            <BatchUploadButton handleFilesSelected={handleSelectedFiles} disabled={shouldDisableBatchAdd} />
            <ResetBatchUpload onClick={resetSelectedFiles} disabled={shouldDisableBatchReset} />
            <SubmitButton onClick={startProcess} disabled={shouldDisableSubmit} />
          </div>
        </section>
        <section className="manager-section manager-row-listing">
          {statusVDom}
        </section>
      </editiondetails-container>
    </editiondetails-wrapper>

  return vDom
}

function ToggleProfile({ onClick, disabled, boolean }) {
  const props = {
    autoName: "conversion.button.toggleProfile",
    disabled,
    onClick,
    selected: false,
    title: "Toggle whether to use the old or new conversion profile"
  }
  const label = boolean ? 'New' : 'Old'
  const vDom =
    <ManagerAction {...props}>
      Profile: {label}
    </ManagerAction>

  return vDom
}

function SubmitButton({ onClick, disabled }) {
  const props = {
    autoName: "conversion.button.submitConversion",
    disabled,
    onClick,
    selected: false,
    title: "Convert the batch of assets to an edition"
  }
  const vDom =
    <ManagerAction {...props}>
      Convert Script
    </ManagerAction>

  return vDom
}

function ResetBatchUpload({ onClick, disabled }) {
  const props = {
    autoName: "conversion.button.resetBatchUpload",
    disabled,
    onClick,
    selected: false,
    title: "Reset the batch of assets to upload"
  }
  const vDom =
    <ManagerAction {...props}>
      Reset Assets
    </ManagerAction>

  return vDom
}

function BatchUploadButton({ handleFilesSelected, disabled }) {
  const { openFilePicker } = useMultiFileInput(
    document,
    BATCH_MEDIA_ACCEPT,
    handleFilesSelected
  )
  const props = {
    autoName: "conversion.button.batchUpload",
    disabled: disabled,
    onClick: openFilePicker,
    selected: false,
    title: "Select the Excel script and all requisite assets at once"
  }
  const vDom =
    <ManagerAction {...props}>
      Batch Assets
    </ManagerAction>

  return vDom
}

function ConversionReady() {
  const style = {
    fontSize: '2rem',
    paddingTop: '8rem',
    textAlign: 'center',
  }
  const props = { style }
  const vDom =
    <div {...props}>
      <h4 className="manager-conversion__ready">
        All required assets are present and ready to convert
      </h4>
    </div>

  return vDom
}

function MissingScriptError() {
  const style = {
    fontSize: '2rem',
    paddingTop: '8rem',
    textAlign: 'center',
  }
  const props = { style }
  const vDom =
    <div {...props}>
      <h4 className="manager-conversion__error">
        Provide an Excel Script to Validate and Convert
      </h4>
    </div>

  return vDom
}

function MissingNameError() {
  const style = {
    fontSize: '2rem',
    paddingTop: '8rem',
    textAlign: 'center',
  }
  const props = { style }
  const vDom =
    <div {...props}>
      <h4 className="manager-conversion__error">
        Provide a name for the edition conversion process to proceed
      </h4>
    </div>

  return vDom
}

function IOError({ message }) {
  const style = {
    fontSize: '2rem',
    paddingTop: '8rem',
    fontColor: 'red',
    textAlign: 'center',
  }
  const props = { style }
  const vDom =
    <div {...props}>
      <h3 className="manager-conversion__error">Encountered an error in the process:</h3>
      <h4 className="manager-conversion__error-details">{message}</h4>
      <h5 className="manager-conversion__error-details">Please correct the issue and retry submitting the Excel file</h5>
    </div>

  return vDom
}

function NetworkError({ message }) {
  const style = {
    fontSize: '2rem',
    paddingTop: '8rem',
    fontColor: 'red',
    textAlign: 'center',
  }
  const props = { style }
  const vDom =
    <div {...props}>
      <h3 className="manager-conversion__error">Encountered an error while uploading a file:</h3>
      <h4 className="manager-conversion__error-details">{message}</h4>
      <h5 className="manager-conversion__error-details">You can retry submitting</h5>
    </div>

  return vDom
}

function ValidationErrors({ errors }) {
  const missingStyle = { color: 'red', fontWeight: 'bold' }

  const vDom =
    <table className="manager-table">
      <thead>
        <tr>
          <th className="manager-table-column">
            <strong className="mananger-table-column__name">
              ROW NUM
            </strong>
          </th>
          <th className="manager-table-column">
            <strong className="mananger-table-column__name">
              Which Template
            </strong>
          </th>
          <th className="manager-table-column">
            <strong className="mananger-table-column__name">
              AUDIO FILENAME
            </strong>
          </th>
          <th className="manager-table-column">
            <strong className="mananger-table-column__name">
              GRAPHIC FILENAME
            </strong>
          </th>
        </tr>
      </thead>
      <tbody>
        {errors.map((error, idx) => (
          <tr key={idx}>
            <td className="manager-table-data">{error.rowNum}</td>
            <td className="manager-table-data">{error.rowTemplate}</td>
            <td className="manager-table-data" style={error.hasExpectedAudio ? undefined : missingStyle}>
              {error.rowExpectedAudio || '—'}
            </td>
            <td className="manager-table-data" style={error.hasExpectedVideo ? undefined : missingStyle}>
              {error.rowExpectedVideo || '—'}
            </td>
          </tr>
        ))}
      </tbody>
    </table>

  return vDom
}

function Process({ completed, total, onCancel }) {
  const style = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: '2rem',
    paddingTop: '8rem',
    textAlign: 'center',
  }
  const divProps = { style }
  const progProps = {
    max: total,
    style: {
      fontSize: '2rem',
    },
    value: completed,
  }
  const btnProps = {
    id: 'manager-conversion__cancel',
    onClick: onCancel,
    style: {
      fontSize: '1rem',
    },
  } 
  const vDom =
    <div {...divProps}>
      <progress {...progProps}></progress>
      <button {...btnProps}>
        Cancel
      </button>
    </div>

  return vDom
}

function isRow(row) {
  const { rowNumber, audioText, audioFileName, graphic } = row

  // ED-1908 - Match rows meant as comments that will populate the ROW NUM
  // column and the GRAPHIC column
  if (rowNumber && !audioText && !audioFileName && graphic) return false

  for (const prop of Object.keys(row)) {
    const value = row[prop]
    if (prop === 'rowNumber') continue
    if (value != null && value !== '') return true
  }
  return false
}

